import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {OndemandVideoOutlined, OndemandVideoRounded} from "@mui/icons-material";
import {matchPath, useHistory, useLocation} from "react-router-dom";
import {routes} from "../../../../../core/models/static/routes/app-routes";
import {EndActionType} from "../index";
import useAccessLevels from "../../../../hooks/use-access-levels";
import {Breakpoint, PopoverActions, Theme, useMediaQuery} from "@mui/material";
import debounce from "lodash/debounce";
import EnvService from "../../../../../core/services/env";
import {useTranslation} from "react-i18next";
import {TFunction} from "i18next";
import {ProductInformationTabs} from "../../../../views/panel/operator/products/single";
import TutorialsPopover from "./tutorials-popover";

interface ITutorial {
    url: string;
    imageUrl?: string;
    title: string;
}

class Tutorial implements ITutorial {
    url: string;
    imageUrl?: string;
    title: string;

    /**
     * Creates a new tutorial object.
     * @param title         The title of the tutorial.
     * @param url           The url of the tutorial.
     * @param imageUrl      The image url of the tutorial.
     */
    constructor(title: string, url: string, imageUrl?: string) {
        this.url = url;
        this.title = title;
        this.imageUrl = imageUrl;
    }

    /**
     * Generates a tutorial object for the BizLearn API.
     * @param id            The video id.
     * @param title         The title of the video.
     * @param imageName     The name of the image associated with the video.
     * @constructor
     */
    static BizLearn(id: number, title: string, imageName?: string): Tutorial {
        const generateUrl = (videoId: number): string => `${EnvService.ApplicationUrls.bizLearn}/courses/6/video/${videoId}?tab=description`;
        const generateImageUrl = (name: string): string => `https://bizlearnAPI.bizkeytech.com/files/${name}`;
        return new Tutorial(title, generateUrl(id), imageName ? generateImageUrl(imageName) : undefined);
    }
}

/**
 * The map of all the available tutorial videos in the system.
 */
export const BizLearnVideos = {
    overview: (t: TFunction<'components'>) => Tutorial.BizLearn(
        36,
        t($ => $['partner-platform-overview']),
        'partner-platform-overview.jpeg'
    ),
    tierPricing: (t: TFunction<'components'>) => Tutorial.BizLearn(
        16,
        t($ => $['partner-platform-tier-pricing']),
        'partner-platform-tier-pricing.webp'
    ),
    surveyTutorial: (t: TFunction<'components'>) => Tutorial.BizLearn(
        17,
        t($ => $['partner-platform-survey-tutorial']),
        'partner-platform-survey-tutorial.webp'
    ),
    manageRegions: (t: TFunction<'components'>) => Tutorial.BizLearn(
        18,
        t($ => $['partner-platform-manage-regions']),
        'partner-platform-manage-regions.webp'
    ),
    promotions: (t: TFunction<'components'>) => Tutorial.BizLearn(
        19,
        t($ => $['partner-platform-promotions']),
        'partner-platform-promotions.webp'
    ),
    importingOrganizationAndPersonalSettings: (t: TFunction<'components'>) => Tutorial.BizLearn(
        26,
        t($ => $['partner-platform-importing-organization-and-personal-settings']),
        'partner-platform-importing-organization-and-personal-settings.webp'
    ),
    giftCardManagement: (t: TFunction<'components'>) => Tutorial.BizLearn(
        27,
        t($ => $['partner-platform-gift-card-management']),
        'partner-platform-gift-card-management.webp'
    ),
    comparisonLists: (t: TFunction<'components'>) => Tutorial.BizLearn(
        28,
        t($ => $['partner-platform-comparison-lists']),
        'partner-platform-comparison-lists.webp'
    ),
    warrantyReturnRequests: (t: TFunction<'components'>) => Tutorial.BizLearn(
        29,
        t($ => $['partner-platform-warranty-return-requests']),
        'partner-platform-warranty-return-requests.webp'
    ),
    managePartSupportRequest: (t: TFunction<'components'>) => Tutorial.BizLearn(
        38,
        t($ => $['partner-platform-manage-part-support-request']),
        'partner-platform-manage-part-support-request.webp'
    ),
    partSupportSettings: (t: TFunction<'components'>) => Tutorial.BizLearn(
        37,
        t($ => $['partner-platform-part-support-settings']),
        'partner-platform-part-support-settings.webp'
    ),
    drivers: (t: TFunction<'components'>) => Tutorial.BizLearn(
        39,
        t($ => $['partner-platform-drivers']),
        'partner-platform-drivers.webp'
    ),
    operators: (t: TFunction<'components'>) => Tutorial.BizLearn(
        40,
        t($ => $['partner-platform-operators']),
        'partner-platform-operators.webp'
    ),
    salesPersonQuotes: (t: TFunction<'components'>) => Tutorial.BizLearn(
        41,
        t($ => $['partner-platform-sales-person-quotes']),
        'partner-platform-sales-person-quotes.webp'
    ),
    salesBackOrders: (t: TFunction<'components'>) => Tutorial.BizLearn(
        42,
        t($ => $['partner-platform-sales-back-orders']),
        'partner-platform-sales-back-orders.jpeg'
    ),
    invoicesCreditsInSales: (t: TFunction<'components'>) => Tutorial.BizLearn(
        51,
        t($ => $['partner-platform-invoices-credits-in-sales']),
        'partner-platform-invoices-credits-in-sales.webp'
    ),
    customerSupportChat: (t: TFunction<'components'>) => Tutorial.BizLearn(
        52,
        t($ => $['partner-platform-customer-support-chat']),
        'partner-platform-customer-support-chat.webp'
    ),
    freeShippingFees: (t: TFunction<'components'>) => Tutorial.BizLearn(
        53,
        t($ => $['partner-platform-free-shipping-fees']),
        'partner-platform-free-shipping-fees.webp'
    ),
    freightCompaniesReports: (t: TFunction<'components'>) => Tutorial.BizLearn(
        54,
        t($ => $['partner-platform-freight-companies-reports']),
        'partner-platform-freight-companies-reports.webp'
    ),
    productsPart1: (t: TFunction<'components'>) => Tutorial.BizLearn(
        55,
        t($ => $['partner-platform-products-part-1']),
        'partner-platform-products-part-1.webp'
    ),
    productsPart2: (t: TFunction<'components'>) => Tutorial.BizLearn(
        57,
        t($ => $['partner-platform-products-part-2']),
        'partner-platform-products-part-2.webp'
    ),
    productsPart3: (t: TFunction<'components'>) => Tutorial.BizLearn(
        56,
        t($ => $['partner-platform-products-part-3']),
        'partner-platform-products-part-3.webp'
    ),
    inventoryAdjustment: (t: TFunction<'components'>) => Tutorial.BizLearn(
        58,
        t($ => $['partner-platform-inventory-adjustment']),
        'partner-platform-inventory-adjustment.webp'
    ),
    productsManageSuggestions: (t: TFunction<'components'>) => Tutorial.BizLearn(
        59,
        t($ => $['partner-platform-products-manage-suggestions']),
        'partner-platform-products-manage-suggestions.webp'
    ),
    bulkPriceChange: (t: TFunction<'components'>) => Tutorial.BizLearn(
        60,
        t($ => $['partner-platform-bulk-price-change']),
        'partner-platform-bulk-price-change.webp'
    ),
    tagManagement: (t: TFunction<'components'>) => Tutorial.BizLearn(
        61,
        t($ => $['partner-platform-tag-management']),
        'partner-platform-tag-management.webp'
    ),
    productsFastConnection: (t: TFunction<'components'>) => Tutorial.BizLearn(
        62,
        t($ => $['partner-platform-products-fast-connection']),
        'partner-platform-products-fast-connection.webp'
    ),
    bannerThemeManagement: (t: TFunction<'components'>) => Tutorial.BizLearn(
        63,
        t($ => $['partner-platform-banner-theme-management']),
        'partner-platform-banner-theme-management.webp'
    ),
    contentManagement: (t: TFunction<'components'>) => Tutorial.BizLearn(
        64,
        t($ => $['partner-platform-content-management']),
        'partner-platform-content-management.webp'
    ),
    customerMembers: (t: TFunction<'components'>) => Tutorial.BizLearn(
        65,
        t($ => $['partner-platform-customer-members']),
        'partner-platform-customer-members.webp'
    ),
    customersListPart1: (t: TFunction<'components'>) => Tutorial.BizLearn(
        66,
        t($ => $['partner-platform-customers-list-part-1']),
        'partner-platform-customers-list-part-1.webp'
    ),
    customersListPart2: (t: TFunction<'components'>) => Tutorial.BizLearn(
        67,
        t($ => $['partner-platform-customers-list-part-2']),
        'partner-platform-customers-list-part-2.webp'
    ),
    homepageEditions: (t: TFunction<'components'>) => Tutorial.BizLearn(
        68,
        t($ => $['partner-platform-homepage-editions']),
        'partner-platform-homepage-editions.webp'
    ),
    invoicesAR: (t: TFunction<'components'>) => Tutorial.BizLearn(
        69,
        t($ => $['partner-platform-invoices-AR']),
        'partner-platform-invoices-AR.webp'
    ),
    operatorProfile: (t: TFunction<'components'>) => Tutorial.BizLearn(
        70,
        t($ => $['partner-platform-operator-profile']),
        'partner-platform-operator-profile.webp'
    ),
    quickOrder: (t: TFunction<'components'>) => Tutorial.BizLearn(
        71,
        t($ => $['partner-platform-quick-order']),
        'partner-platform-quick-order.webp'
    ),
    printCatalogue: (t: TFunction<'components'>) => Tutorial.BizLearn(
        91,
        t($ => $['partner-platform-printing-catalogue']),
        'partner-platform-printing-catalogue.webp'
    ),
    nexusSearch: (t: TFunction<'components'>) => new Tutorial(
        t($ => $['mastering-nexus-search']),
        "https://youtu.be/0opjuvP4FHI?si=cvUXvLH2WG1LSigq",
        'https://i9.ytimg.com/vi_webp/0opjuvP4FHI/mqdefault.webp?v=671ad904&sqp=CLjx77wG&rs=AOn4CLCyfo3ruS-l_1QZefsOJIa5LRwREw'
    ),
    v3_3Features: (t: TFunction<'components'>) => Tutorial.BizLearn(
        111,
        t($ => $['partner-platform-v.3.3-features']),
        'partner-platform-v.3.3-features.jpg'
    ),
    quickOrderAIIntegration: (t: TFunction<'components'>) => Tutorial.BizLearn(
        112,
        t($ => $['partner-platform-quick-order-ai-integration']),
        'partner-platform-quick-order-ai-integration.jpg'
    ),
    warehouseSchedulingModule: (t: TFunction<'components'>) => Tutorial.BizLearn(
        113,
        t($ => $['partner-platform-warehouse-scheduling-module']),
        'partner-platform-warehouse-scheduling-module.jpg'
    ),
    version3_7_1Features: (t: TFunction<'components'>) => Tutorial.BizLearn(
        116,
        t($ => $['operator-platform-version3-7-1-features-&-improvements']),
        'operator-platform-version3-7-1-features-&-improvements.jpg'
    ),
    version3_8_0Features: (t: TFunction<'components'>) => Tutorial.BizLearn(
        120,
        t($ => $['operator-platform-version3-8-0']),
        'operator-platform-version-3-8-0.webp'
    ),
    version3_9_0Features: (t: TFunction<'components'>) => Tutorial.BizLearn(
        121,
        t($ => $['operator-platform-version3-9-0']),
        'operator-platform-version-3-9-0.jpg'
    ),
    version3_9_1Features: (t: TFunction<'components'>) => Tutorial.BizLearn(
        122,
        t($ => $['operator-platform-version3-9-1']),
        'operator-platform-version-3-9-1.jpg'
    ),
    version3_9_2Features: (t: TFunction<'components'>) => Tutorial.BizLearn(
        123,
        t($ => $['operator-platform-version3-9-2']),
        'operator-platform-version-3-9-2.webp'
    ),
    customerAccountReceivableTransactions: (t: TFunction<'components'>) => Tutorial.BizLearn(
        124,
        t($ => $['operator-platform-customer-account-receivable-transactions']),
        'operator-platform-customer-account-receivable-transactions.webp'
    ),
    arDashboard: (t: TFunction<'components'>) => Tutorial.BizLearn(
        125,
        // t($ => $['operator-platform-ar-dashboard']),
        "Operator Platform - AR Dashboard",
        'operator-platform-ar-dashboard.webp'
    ),
    bulkImportPropertiesOptions: (t: TFunction<'components'>) => Tutorial.BizLearn(
        126,
        "Operator Platform - Bulk Import Properties / Property Options",
        'operator-platform-bulk-import-Properties-options.jpg'
    ),
    exportingImportingDashboards: (t: TFunction<'components'>) => Tutorial.BizLearn(
        128,
        "Operator Platform - Exporting & Importing Dashboards",
        'operator-platform-exporting-importing-dashboards.jpg'
    ),
    importingExternalCustomers: (t: TFunction<'components'>) => Tutorial.BizLearn(
        129,
        "Operator Platform - Importing External Customers",
        'operator-platform-importing-external-customers.jpg'
    ),
    importingExternalProducts: (t: TFunction<'components'>) => Tutorial.BizLearn(
        130,
        "Operator Platform - Importing External Products",
        'operator-platform-importing-external-products.jpg'
    ),
    manageOrganizationInfo: (t: TFunction<'components'>) => Tutorial.BizLearn(
        132,
        "Operator Platform - Manage Organization Info",
        'operator-platform-manage-organization-information.jpg'
    ),
    taxesForProductWarehouse: (t: TFunction<'components'>) => Tutorial.BizLearn(
        133,
        "Operator Platform - Taxes for Product-Warehouse",
        'operator-platform-manage-taxes-for-specific-product-warehouse.jpg'
    ),
    importingPausingAndCorrection: (t: TFunction<'components'>) => Tutorial.BizLearn(
        134,
        "Operator Platform - Importing Pausing & Correction",
        'operator-platform-importing-pausing-and-correction.jpg'
    ),
    personalAndPanelSettings: (t: TFunction<'components'>) => Tutorial.BizLearn(
        135,
        "Operator Platform - Personal & Panel Settings",
        'operator-platform-personal-and-panel-settings.jpg'
    ),
    pricingFormulas: (t: TFunction<'components'>) => Tutorial.BizLearn(
        136,
        "Operator Platform - Pricing Formulas",
        'operator-platform-pricing-formulas.jpg'
    ),
    productVisibilityControls: (t: TFunction<'components'>) => Tutorial.BizLearn(
        137,
        "Operator Platform - Product Visibility Controls",
        'operator-platform-product-visibility-controls.jpg'
    ),
    productFormulaUpdates: (t: TFunction<'components'>) => Tutorial.BizLearn(
        138,
        "Operator Platform - v3.12 Product Formula Updates",
        'operator-platform-v3-12-product-formula-updates.webp'
    ),
    multipleUPCsPerProduct: (t: TFunction<'components'>) => Tutorial.BizLearn(
        139,
        "Operator Platform - Multiple UPCs Per Product",
        'operator-platform-multiple-upcs-per-product.webp'
    ),
    productTitles: (t: TFunction<'components'>) => Tutorial.BizLearn(
        140,
        "Operator Platform - Product Titles",
        'operator-platform-product-titles.webp'
    ),
    formsAndProductForms: (t: TFunction<'components'>) => Tutorial.BizLearn(
        141,
        "Operator Platform - Forms and Product Forms",
        'operator-platform-forms-and-product-forms.webp'
    ),
    suggestiveSelling: (t: TFunction<'components'>) => Tutorial.BizLearn(
        142,
        "Operator Platform - Suggestive Selling",
        'operator-platform-suggestive-selling.webp'
    ),
    preventativeInteraction: (t: TFunction<'components'>) => Tutorial.BizLearn(
        143,
        "Operator Platform - Preventative Interaction",
        'operator-platform-preventative-interaction.webp'
    ),
    bulkOrderEligibilityImport: (t: TFunction<'components'>) => Tutorial.BizLearn(
        144,
        "Operator Platform - Bulk Order Eligibility Import",
        'bulk-online-ordering-eligibility-assignment.jpg'
    ),
    bulkProductWarehouseAssignment: (t: TFunction<'components'>) => Tutorial.BizLearn(
        145,
        "Operator Platform - Bulk Product Whs Assignment",
        'bulk-product-warehouse-assignment.jpg'
    ),
    bulkProductTagAssignment: (t: TFunction<'components'>) => Tutorial.BizLearn(
        146,
        "Operator Platform - Bulk Product Tag Assignment",
        'bulk-product-tag-assignment.jpg'
    ),
    productDeliveryMode: (t: TFunction<'components'>) => Tutorial.BizLearn(
        147,
        "Operator Platform - Manage Products' Delivery Mode",
        'product-delivery-mode-management.jpg'
    ),
    dynamicPageManagement: (t: TFunction<'components'>) => Tutorial.BizLearn(
        148,
        "Operator Platform - Dynamic Page Management",
        'dynamic-page-management.jpg'
    ),
    ordersTermsCodeEdition: (t: TFunction<'components'>) => Tutorial.BizLearn(
        149,
        "Operator Platform - Orders Payment terms Edition",
        'edition-of-orders-terms-code.jpg'
    ),
    exportProductsBasicDetails: (t: TFunction<'components'>) => Tutorial.BizLearn(
        150,
        "Operator Platform - Export Products Basic Details",
        'exporting-products-basic-details.jpg'
    ),
    freightEstimationInOrders: (t: TFunction<'components'>) => Tutorial.BizLearn(
        151,
        "Operator Platform - Freight Estimation In Orders",
        'Freight-estimation-in-orders.jpg'
    ),
    importOtherPartnersProducts: (t: TFunction<'components'>) => Tutorial.BizLearn(
        152,
        "Operator Platform - Import Other partners Products",
        'importing-products-from-another-partner.jpg'
    ),
    bulkProductFormAssignments: (t: TFunction<'components'>) => Tutorial.BizLearn(
        153,
        "Operator Platform - Bulk Product Form Assignments",
        'bulk-product-form-assignment.jpg'
    ),
    regionRatesAndSystemRegions: (t: TFunction<'components'>) => Tutorial.BizLearn(
        154,
        "Operator Platform - Region Rates & System Regions",
        'region-rates-and-regions.jpg'
    ),
    formulasVisibilityToggles: (t: TFunction<'components'>) => Tutorial.BizLearn(
        155,
        "Operator Platform - Formulas Visibility Toggles",
        'visual-toggler-for-formulas.jpg'
    ),
    newImportingPageUI: (t: TFunction<'components'>) => Tutorial.BizLearn(
        156,
        "Operator Platform - New Importing Page UI",
        'operator-Platform-new-importing-page-ui.webp'
    ),
    bulkProductRelationAssignment: (t: TFunction<'components'>) => Tutorial.BizLearn(
        157,
        "Operator Platform - Bulk Product Relation Assignment",
        'operator-platform-bulk-product-relation-assign.webp'
    ),
    bulkProductPartnerVisibilityAssignment: (t: TFunction<'components'>) => Tutorial.BizLearn(
        158,
        "Bulk Product Partner Visibility Assignment",
        'bulk-product-partner-visibility-assignment.webp'
    ),
    bulkProductCodesAssignment: (t: TFunction<'components'>) => Tutorial.BizLearn(
        159,
        "Operator Platform - Bulk Product Codes Assignment",
        'operator-platform-bulk-product-codes-assignment.webp'
    ),
    grossProfitsInBizKeyBI: (t: TFunction<'components'>) => Tutorial.BizLearn(
        160,
        "Operator Platform - Gross Profits in BizKey BI",
        'operator-platform-gross-profits-in-bizkey-bi.webp'
    ),
    inventoryTransfers: (t: TFunction<'components'>) => Tutorial.BizLearn(
        161,
        "Operator Platform - Inventory Transfers",
        'operator-platform-inventory-transfers.webp'
    ),
    customerOnboardingUpdates: (t: TFunction<'components'>) => Tutorial.BizLearn(
        162,
        "Operator Platform - Customer Onboarding Updates",
        'operator-platform-customer-onboarding-updates.webp'
    ),
    productRemovalsFeature: (t: TFunction<'components'>) => Tutorial.BizLearn(
        163,
        "Operator Platform - Product Removals Feature",
        'operator-platform-product-removals-feature.webp'
    ),
    systemSearch: (t: TFunction<'components'>) => Tutorial.BizLearn(
        164,
        "Operator Platform - System Search",
        'operator-platform-system-search.webp'
    ),
    vinDecoderFeature: (t: TFunction<'components'>) => Tutorial.BizLearn(
        165,
        "Operator Platform - VIN Decoder Feature",
        'operator-platform-VIN-decoder-feature.webp'
    ),
    defaultIndustryConfig: (t: TFunction<'components'>) => Tutorial.BizLearn(
        166,
        "Operator Platform - Default Industry Config",
        'operator-platform-default-industry-config.webp'
    ),
    productsShippingFlatRates: (t: TFunction<'components'>) => Tutorial.BizLearn(
        167,
        "Operator Platform - Products' Shipping Flat Rates",
        'operator-platform-products-shipping-flat-rates.webp'
    ),
    bulkImportProductFlatRates: (t: TFunction<'components'>) => Tutorial.BizLearn(
        168,
        "Operator Platform - Bulk Import Product Flat Rates",
        'operator-platform-bulk-import-product-flat-rates.webp'
    ),
    freightModuleUpdates: (t: TFunction<'components'>) => Tutorial.BizLearn(
        169,
        "Operator Platform - Freight Module Updates",
        'operator-platform-freight-module-updates.webp'
    ),
}

const useBizLearnVideosEndAction = (breakpoint: Breakpoint): (EndActionType | null) => {
    const history = useHistory();
    const [applicableTutorials, setApplicableTutorials] = useState<Array<ITutorial>>([]);
    const [popoverAnchor, setPopoverAnchor] = useState();
    const passedBreakpoint = useMediaQuery<Theme>((theme) => theme.breakpoints.up(breakpoint));
    const popoverAction = useRef<PopoverActions>(null);
    const operatorAccessLevels = useAccessLevels();
    const {t} = useTranslation("components", {keyPrefix: 'panel-sections.app-bar.actions.biz-learn-videos'});

    /**
     * Builds the full catalog of Biz Learn videos (deduped by url, stable catalog key order).
     */
    const allTutorials = useMemo(() => {
        const seen = new Set<string>();
        const tutorials: ITutorial[] = [];
        for (const buildTutorial of Object.values(BizLearnVideos)) {
            const tutorial = buildTutorial(t);
            if (seen.has(tutorial.url)) {
                continue;
            }
            seen.add(tutorial.url);
            tutorials.push(tutorial);
        }
        return tutorials;
    }, [t]);

    /**
     * Fetches the params associated with the current pathname and the given route.
     * @param {string} pathname
     * @param {string | string[]} route
     * @return {{[p: string]: any} | undefined}
     */
    const getParams = useCallback((pathname: string, route: string | string[]) => {
        return matchPath(pathname, {
            path: route,
            exact: true,
        })?.params;
    }, [])

    /**
     * Fetches the main tutorials for the current page.
     */
    const getMainTutorialsFromPage = useCallback((location: ReturnType<typeof useLocation>) => {
        const tutorials: Array<ITutorial> = [
            BizLearnVideos.customerSupportChat(t),
            BizLearnVideos.quickOrderAIIntegration(t),
        ];
        switch (location.pathname) {
            case routes.analytics.systemDashboards: {
                tutorials.push(BizLearnVideos.overview(t));
                break;
            }
            case routes.settings.priceMatrix.list:
            case routes.settings.priceMatrix.customers: {
                tutorials.push(BizLearnVideos.tierPricing(t));
                break
            }
            case routes.userPlatform.surveys.list: {
                tutorials.push(BizLearnVideos.surveyTutorial(t));
                break;
            }
            case routes.settings.regions: {
                tutorials.push(BizLearnVideos.manageRegions(t));
                break;
            }
            case routes.sales.promotions.list: {
                tutorials.push(BizLearnVideos.promotions(t));
                break;
            }
            case routes.settings.import:
            case routes.settings.personal:
            case routes.settings.organization: {
                tutorials.push(BizLearnVideos.importingOrganizationAndPersonalSettings(t));
                break;
            }
            case routes.settings.giftCards.list:
            case routes.settings.giftCards.categories: {
                tutorials.push(BizLearnVideos.giftCardManagement(t));
                break;
            }
            case routes.lookup.compare: {
                tutorials.push(BizLearnVideos.comparisonLists(t));
                break;
            }
            case routes.lookup.productInformation: {
                tutorials.push(BizLearnVideos.warehouseSchedulingModule(t));
                break;
            }
            case routes.warrantyAndReturn.requests: {
                tutorials.push(BizLearnVideos.warrantyReturnRequests(t));
                break;
            }
            case routes.warrantyAndReturn.settings.process:
            case routes.warrantyAndReturn.settings.statuses: {
                tutorials.push(BizLearnVideos.partSupportSettings(t));
                break;
            }
            case routes.drivers: {
                tutorials.push(BizLearnVideos.drivers(t));
                break;
            }
            case routes.operators.list:
            case routes.operators.dashboards.base:
            case routes.operators.groups.base: {
                tutorials.push(BizLearnVideos.operators(t));
                break;
            }
            case routes.salesPerson.dashboard:
            case routes.sales.quotes: {
                tutorials.push(BizLearnVideos.salesPersonQuotes(t));
                break;
            }
            case routes.sales.backOrder:
            case routes.sales.salesOrder: {
                tutorials.push(BizLearnVideos.salesBackOrders(t));
                break;
            }
            case routes.sales.invoices:
            case routes.sales.credits: {
                tutorials.push(BizLearnVideos.invoicesCreditsInSales(t));
                break;
            }
            case routes.shippingFees.settings:
            case routes.shippingFees.list: {
                tutorials.push(BizLearnVideos.freeShippingFees(t));
                break;
            }
            case routes.shippingFees.companies:
            case routes.shippingFees.reports: {
                tutorials.push(BizLearnVideos.freightCompaniesReports(t));
                break;
            }
            case routes.products.list: {
                tutorials.push(BizLearnVideos.productsPart1(t));
                break;
            }
            case routes.products.relationTypes: {
                tutorials.push(BizLearnVideos.productsPart3(t));
                break;
            }
            case routes.inventory.inventoryAdjustment.list:
            case routes.inventory.inventoryAdjustment.items:
            case routes.inventory.inventoryAdjustment.report:
            case routes.inventory.inventoryAdjustment.bulkAvailabilityChange: {
                tutorials.push(BizLearnVideos.inventoryAdjustment(t));
                break;
            }
            case routes.products.manageSuggestions.technical:
            case routes.products.manageSuggestions.application: {
                tutorials.push(BizLearnVideos.productsManageSuggestions(t));
                break;
            }
            case routes.products.bulkPriceChange.dashboard: {
                tutorials.push(BizLearnVideos.bulkPriceChange(t));
                break;
            }
            case routes.products.tags.list: {
                tutorials.push(BizLearnVideos.tagManagement(t));
                break;
            }
            case routes.products.productFastConnection: {
                tutorials.push(BizLearnVideos.productsFastConnection(t));
                break;
            }
            case routes.userPlatform.bannerManagement:
            case routes.userPlatform.themeManagement.web:
            case routes.userPlatform.themeManagement.phone: {
                tutorials.push(BizLearnVideos.bannerThemeManagement(t));
                break;
            }
            case routes.userPlatform.contentManagement: {
                tutorials.push(BizLearnVideos.contentManagement(t));
                break;
            }
            case routes.customers.list:
            case routes.customers.members.base:
            case routes.customers.members.activeMembers:
            case routes.customers.members.inactiveMembers:
            case routes.customers.members.passiveMembers: {
                tutorials.push(BizLearnVideos.customerMembers(t));
                break;
            }

            case routes.userPlatform.liveHomepages:
            case routes.userPlatform.list: {
                tutorials.push(BizLearnVideos.homepageEditions(t));
                break;
            }

            case routes.accounting.invoices:
            case routes.accounting.accountReceivable: {
                tutorials.push(BizLearnVideos.invoicesAR(t));
                break;
            }

            case routes.operatorProfile: {
                tutorials.push(BizLearnVideos.operatorProfile(t));
                break;
            }
            case routes.printing.printCatalogue: {
                tutorials.push(BizLearnVideos.printCatalogue(t));
                break;
            }
            case routes.lookup.search: {
                tutorials.push(BizLearnVideos.nexusSearch(t));
                break;
            }
            default: {
                let params;

                params = getParams(location.pathname, [routes.userPlatform.surveys.surveyPreview]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.surveyTutorial(t));
                    break;
                }

                params = getParams(location.pathname, [routes.operators.dashboards.single]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.operators(t));
                    break;
                }

                params = getParams(location.pathname, [routes.warrantyAndReturn.single]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.managePartSupportRequest(t));
                    break;
                }

                params = getParams(location.pathname, [routes.operators.groups.single]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.operators(t));
                    break;
                }

                params = getParams(location.pathname, [
                    routes.customer.sales.quotes.base,
                    routes.customer.sales.quotes.single,
                ]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.salesPersonQuotes(t));
                    break;
                }

                params = getParams(location.pathname, [
                    routes.customer.sales.salesOrder.base,
                    routes.customer.sales.salesOrder.single,
                    routes.customer.sales.backOrder.base,
                    routes.customer.sales.backOrder.single,
                ]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.salesBackOrders(t));
                    break;
                }

                params = getParams(location.pathname, [
                    routes.customer.sales.invoices.base,
                    routes.customer.sales.invoices.single,
                    routes.customer.sales.credits.base,
                    routes.customer.sales.credits.single,
                ]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.invoicesCreditsInSales(t));
                    break;
                }

                params = getParams(location.pathname, [routes.customer.shippingFees.list, routes.customer.shippingFees.settings]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.freeShippingFees(t));
                    break;
                }

                params = getParams(location.pathname, [routes.products.single]);
                if (!!params) {
                    switch (params.tab) {
                        case ProductInformationTabs.basic:
                        case ProductInformationTabs.price:
                            tutorials.push(BizLearnVideos.productsPart1(t));
                            break;
                        case ProductInformationTabs.availability:
                            tutorials.push(BizLearnVideos.productsPart1(t));
                            tutorials.push(BizLearnVideos.warehouseSchedulingModule(t));
                            break;
                        case ProductInformationTabs.technical:
                        case ProductInformationTabs.images:
                        case ProductInformationTabs.feedback:
                            tutorials.push(BizLearnVideos.productsPart2(t));
                            break;
                        case ProductInformationTabs.inventoryAdjustmentReport:
                        case ProductInformationTabs.inventoryAdjustmentRecord:
                            tutorials.push(BizLearnVideos.inventoryAdjustment(t));
                            break;
                        case ProductInformationTabs.technicalFiles:
                        case ProductInformationTabs.notes:
                        case ProductInformationTabs.relation:
                            tutorials.push(BizLearnVideos.productsPart2(t));
                            break;
                        case ProductInformationTabs.dimensions:
                            // TODO: add video for dimensions tab when available
                            break;

                        default:
                            if (params.tab?.startsWith("property-group")) {
                                tutorials.push(BizLearnVideos.productsPart2(t));
                                break;
                            }
                            break;
                    }
                }

                params = getParams(location.pathname, [routes.inventory.inventoryAdjustment.single]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.inventoryAdjustment(t));
                    break;
                }

                params = getParams(location.pathname, [routes.products.bulkPriceChange.report, routes.products.bulkPriceChange.newBulkPrice]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.bulkPriceChange(t));
                    break;
                }

                params = getParams(location.pathname, [routes.products.tags.single]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.tagManagement(t));
                    break;
                }

                params = getParams(location.pathname, [
                    routes.customer.generalInformation.profile,
                    routes.customer.generalInformation.addresses,
                    routes.customer.generalInformation.loginLogs,
                    routes.customer.generalInformation.promotions,
                    routes.customer.priceMatrix,
                ]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.customersListPart1(t));
                    break;
                }
                params = getParams(location.pathname, [
                    routes.customer.sales.shoppingCart,
                    routes.customer.generalInformation.paymentMethods,
                    routes.customer.shippingFees.list,
                    routes.customer.priceMatrix,
                ]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.customersListPart2(t));
                    break;
                }

                params = getParams(location.pathname, [
                    routes.userPlatform.phoneHomepage,
                    routes.userPlatform.webHomepage,
                ]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.homepageEditions(t));
                    break;
                }

                params = getParams(location.pathname, [
                    routes.customer.accounting.invoices.base,
                    routes.customer.accounting.invoices.single,
                ]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.invoicesAR(t));
                    break;
                }

                break;
            }
        }
        return tutorials;
    }, [getParams, t])

    /**
     * Determines the tutorial url based on the location of the history.
     */
    const getTutorialsFromPage = useCallback((location: ReturnType<typeof useLocation>) => {
        const tutorials = getMainTutorialsFromPage(location);

        // case for v3.3.0 features
        switch (location.pathname) {
            case routes.sales.quotes:
            case routes.sales.invoices:
            case routes.accounting.invoices:
                tutorials.push(BizLearnVideos.v3_3Features(t));
                break;
            default:
                const params = getParams(location.pathname, [
                    routes.customer.sales.quotes.base,
                    routes.customer.sales.quotes.single,
                    routes.customer.sales.invoices.base,
                    routes.customer.sales.invoices.single,
                    routes.customer.accounting.invoices.base,
                    routes.customer.accounting.invoices.single,
                ]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.v3_3Features(t));
                    break;
                }
                break;
        }

        let params;

        // case for v3.7.1 features
        switch (location.pathname) {
            case routes.inventory.availabilityThresholds:
            case routes.settings.import:
                tutorials.push(BizLearnVideos.version3_7_1Features(t));
                break;
            default:
                let params;
                params = getParams(location.pathname, [routes.products.single]);
                if (!!params && params?.tab === ProductInformationTabs.dimensions) {
                    tutorials.push(BizLearnVideos.version3_7_1Features(t));
                }
                break;
        }

        // case for v3.8.0 features
        switch (location.pathname) {
            case routes.settings.import:
                tutorials.push(BizLearnVideos.version3_8_0Features(t));
                break;
            default:
                params = getParams(location.pathname, [routes.products.single]);
                if (!!params && params?.tab === ProductInformationTabs.dimensions) {
                    tutorials.push(BizLearnVideos.version3_8_0Features(t));
                }
                params = getParams(location.pathname, [
                    routes.customer.sales.salesOrder.single,
                    routes.customer.sales.invoices.single,
                ]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.version3_8_0Features(t));
                }
                break;
        }

        // case for v3.9.0 features
        switch (location.pathname) {
            case routes.settings.import:
            case routes.printing.printBrochure:
            case routes.shippingFees.reports:
                tutorials.push(BizLearnVideos.version3_9_0Features(t));
                break;
            default:
                params = getParams(location.pathname, [routes.products.single]);
                if (!!params && params?.tab === ProductInformationTabs.vendors) {
                    tutorials.push(BizLearnVideos.version3_9_0Features(t));
                }
                if (!!params && params?.tab === ProductInformationTabs.dimensions) {
                    tutorials.push(BizLearnVideos.version3_9_0Features(t));
                }
                break;
        }

        // case for v3.9.1 features
        switch (location.pathname) {
            case routes.settings.import:
            case routes.settings.export:
            case routes.printing.printBrochure:
                tutorials.push(BizLearnVideos.version3_9_1Features(t));
                break;

        }

        // case for v3.9.2 features
        switch (location.pathname) {
            case routes.settings.organization:
                tutorials.push(BizLearnVideos.version3_9_2Features(t));
                break;
            default:
                const params = getParams(location.pathname, [
                    routes.customer.sales.quotes.base,
                    routes.customer.sales.quotes.single,
                    routes.customer.sales.salesOrder.base,
                    routes.customer.sales.salesOrder.single,
                    routes.customer.sales.invoices.base,
                    routes.customer.sales.invoices.single,
                    routes.customer.accounting.invoices.base,
                    routes.customer.accounting.invoices.single,
                ]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.version3_9_2Features(t));
                    break;
                }
        }

        // customer account receivable transactions
        switch (location.pathname) {
            case routes.accounting.accountReceivable:
                tutorials.push(BizLearnVideos.customerAccountReceivableTransactions(t))
                break;
            default:
                const params = getParams(location.pathname, [routes.customer.accounting.accountReceivableTransactions]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.customerAccountReceivableTransactions(t));
                    break;
                }
        }

        // arDashboard
        switch (location.pathname) {
            case routes.analytics.dashboards:
                tutorials.push(BizLearnVideos.arDashboard(t))
                break;
            default:
                break;
        }

        // bulk import properties options
        switch (location.pathname) {
            case routes.settings.import:
            case routes.products.manageProperties:
                tutorials.push(BizLearnVideos.bulkImportPropertiesOptions(t))
                break;
            default:
                break;
        }

        // exporting & importing dashboards
        switch (location.pathname) {
            case routes.analytics.dashboards:
                tutorials.push(BizLearnVideos.exportingImportingDashboards(t))
                break;
            default:
                break;
        }

        // importing external customers
        switch (location.pathname) {
            case routes.settings.import:
            case routes.customers.list:
                tutorials.push(BizLearnVideos.importingExternalCustomers(t))
                break;
            default:
                break;
        }

        // importing external products
        switch (location.pathname) {
            case routes.settings.import:
            case routes.products.list:
                tutorials.push(BizLearnVideos.importingExternalProducts(t))
                break;
            default:
                break;
        }


        // managing organization info
        switch (location.pathname) {
            case routes.partnerProfile:
                tutorials.push(BizLearnVideos.manageOrganizationInfo(t))
                break;
            default:
                break;
        }

        // managing taxes for specific product-warehouse
        switch (location.pathname) {
            default:
                const params: any = getParams(location.pathname, [routes.products.single]);
                if (!!params && params?.tab === ProductInformationTabs.availability) {
                    tutorials.push(BizLearnVideos.taxesForProductWarehouse(t));
                }
                break;
        }

        // importing pausing and correction
        switch (location.pathname) {
            case routes.settings.import:
                tutorials.push(BizLearnVideos.importingPausingAndCorrection(t))
                break;
            default:
                break;
        }

        // personal and panel settings
        switch (location.pathname) {
            case routes.settings.personal:
                tutorials.push(BizLearnVideos.personalAndPanelSettings(t))
                break;
            default:
                break;
        }

        // pricing formulas
        switch (location.pathname) {
            default:
                const params: any = getParams(location.pathname, [routes.products.single]);
                if (!!params && params?.tab === ProductInformationTabs.price) {
                    tutorials.push(BizLearnVideos.pricingFormulas(t));
                }
                break;
        }

        // product visibility controls
        switch (location.pathname) {
            default:
                const params: any = getParams(location.pathname, [routes.products.single]);
                if (!!params && params?.tab === ProductInformationTabs.basic) {
                    tutorials.push(BizLearnVideos.productVisibilityControls(t));
                }
                break;
        }

        // productFormulaUpdates
        switch (location.pathname) {
            default:
                const params: any = getParams(location.pathname, [routes.products.single]);
                if (!!params && params?.tab === ProductInformationTabs.price) {
                    tutorials.push(BizLearnVideos.productFormulaUpdates(t));
                }
                break;
        }

        // multipleUPCsPerProduct
        switch (location.pathname) {
            case routes.settings.import:
                tutorials.push(BizLearnVideos.multipleUPCsPerProduct(t))
                break;
            default:
                const params: any = getParams(location.pathname, [routes.products.single]);
                if (!!params && (params?.tab === ProductInformationTabs.codes || params?.tab === ProductInformationTabs.basic)) {
                    tutorials.push(BizLearnVideos.multipleUPCsPerProduct(t));
                }
                break;
        }

        // productTitles
        switch (location.pathname) {
            case routes.settings.import:
                tutorials.push(BizLearnVideos.productTitles(t))
                break;
            default:
                const params: any = getParams(location.pathname, [routes.products.single]);
                if (!!params && params?.tab === ProductInformationTabs.basic) {
                    tutorials.push(BizLearnVideos.productTitles(t));
                }
                break;
        }

        // formsAndProductForms
        switch (location.pathname) {
            case routes.forms.list:
            case routes.forms.responses:
                tutorials.push(BizLearnVideos.formsAndProductForms(t))
                break;
            default:
                params = getParams(location.pathname, [
                    routes.customer.forms.responses,
                ]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.formsAndProductForms(t));
                    break;
                }
        }

        // suggestiveSelling
        switch (location.pathname) {
            case routes.products.relationTypes:
                tutorials.push(BizLearnVideos.suggestiveSelling(t))
                break;
            default:
                const params: any = getParams(location.pathname, [routes.products.single]);
                if (!!params && params?.tab === ProductInformationTabs.relation) {
                    tutorials.push(BizLearnVideos.suggestiveSelling(t));
                }
                break;
        }

        // preventativeInteraction
        switch (location.pathname) {
            case routes.settings.import:
                tutorials.push(BizLearnVideos.preventativeInteraction(t))
                break;
        }

        // bulkOrderEligibilityImport
        switch (location.pathname) {
            case routes.settings.import:
                tutorials.push(BizLearnVideos.bulkOrderEligibilityImport(t))
                break;
            default:
                break;
        }

        // bulkProductWarehouseAssignment
        switch (location.pathname) {
            case routes.settings.import:
                tutorials.push(BizLearnVideos.bulkProductWarehouseAssignment(t))
                break;
            default:
                break;
        }

        // bulkProductTagAssignment
        switch (location.pathname) {
            case routes.settings.import:
                tutorials.push(BizLearnVideos.bulkProductTagAssignment(t))
                break;
            default:
                break;
        }

        // productDeliveryMode
        switch (location.pathname) {
            case routes.settings.import:
            case routes.products.list:
                tutorials.push(BizLearnVideos.productDeliveryMode(t))
                break;
            default:
                params = getParams(location.pathname, [routes.products.single, routes.lookup.productInformation]);
                if (!!params && (!params.tab || params.tab === ProductInformationTabs.basic)) {
                    tutorials.push(BizLearnVideos.productDeliveryMode(t));
                }
                break;
        }

        // dynamicPageManagement
        switch (location.pathname) {
            case routes.userPlatform.dynamicPages.list:
                tutorials.push(BizLearnVideos.dynamicPageManagement(t))
                break;
            default:
                params = getParams(location.pathname, [routes.userPlatform.dynamicPages.single]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.dynamicPageManagement(t));
                }
                break;
        }

        // ordersTermsCodeEdition
        switch (location.pathname) {
            default:
                params = getParams(location.pathname, [
                    routes.customer.sales.quotes.single,
                    routes.customer.sales.salesOrder.single,
                ]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.ordersTermsCodeEdition(t));
                }
                break;
        }

        // exportProductsBasicDetails
        switch (location.pathname) {
            case routes.settings.export:
                tutorials.push(BizLearnVideos.exportProductsBasicDetails(t))
                break;
            default:
                break;
        }

        // freightEstimationInOrders
        switch (location.pathname) {
            case routes.shippingFees.regionRates:
            case routes.shippingFees.shippingRates.base:
            case routes.shippingFees.shippingRates.regionRates:
            case routes.shippingFees.shippingRates.productFlatRates:
            case routes.settings.organization:
            case routes.settings.regions:
                tutorials.push(BizLearnVideos.freightEstimationInOrders(t))
                break;
            default:
                params = getParams(location.pathname, [
                    routes.customer.sales.quotes.single,
                    routes.customer.sales.salesOrder.single,
                    routes.customer.sales.invoices.single,
                    routes.customer.accounting.invoices.single,
                ]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.freightEstimationInOrders(t));
                }
                break;
        }

        // importOtherPartnersProducts
        switch (location.pathname) {
            case routes.settings.import:
                tutorials.push(BizLearnVideos.importOtherPartnersProducts(t))
                break;
            default:
                break;
        }

        // bulkProductFormAssignments
        switch (location.pathname) {
            case routes.settings.import:
                tutorials.push(BizLearnVideos.bulkProductFormAssignments(t))
                break;
            default:
                break;
        }

        // regionRatesAndSystemRegions
        switch (location.pathname) {
            case routes.shippingFees.regionRates:
            case routes.shippingFees.shippingRates.base:
            case routes.shippingFees.shippingRates.regionRates:
            case routes.shippingFees.shippingRates.productFlatRates:
            case routes.settings.regions:
                tutorials.push(BizLearnVideos.regionRatesAndSystemRegions(t))
                break;
            default:
                break;
        }

        // formulasVisibilityToggles
        switch (location.pathname) {
            default:
                params = getParams(location.pathname, [routes.products.single]);
                if (!!params && params?.tab === ProductInformationTabs.price) {
                    tutorials.push(BizLearnVideos.formulasVisibilityToggles(t));
                }
                break;
        }

        // newImportingPageUI
        switch (location.pathname) {
            case routes.settings.import:
                tutorials.push(BizLearnVideos.newImportingPageUI(t))
                break;
            default:
                break;
        }

        // bulkProductRelationAssignment
        switch (location.pathname) {
            case routes.settings.import:
                tutorials.push(BizLearnVideos.bulkProductRelationAssignment(t))
                break;
            default:
                break;
        }

        // bulkProductPartnerVisibilityAssignment
        switch (location.pathname) {
            case routes.settings.import:
                tutorials.push(BizLearnVideos.bulkProductPartnerVisibilityAssignment(t))
                break;
            default:
                break;
        }

        // bulkProductCodesAssignment
        switch (location.pathname) {
            case routes.settings.import:
                tutorials.push(BizLearnVideos.bulkProductCodesAssignment(t))
                break;
            default:
                break;
        }

        // grossProfitsInBizKeyBI
        switch (location.pathname) {
            case routes.analytics.dashboards:
                tutorials.push(BizLearnVideos.grossProfitsInBizKeyBI(t))
                break;
            default:
                break;
        }

        // inventoryTransfers
        switch (location.pathname) {
            case routes.inventory.inventoryTransfer.list:
            case routes.inventory.inventoryTransfer.batches:
            case routes.inventory.inventoryTransfer.historyList:
            case routes.inventory.inventoryTransfer.historyItems:
                tutorials.push(BizLearnVideos.inventoryTransfers(t))
                break;
            default:
                params = getParams(location.pathname, [
                    routes.inventory.inventoryTransfer.single,
                    routes.inventory.inventoryTransfer.batchInformation,
                    routes.inventory.inventoryTransfer.postTransfer,
                    routes.inventory.inventoryTransfer.returnInformation,
                    routes.inventory.inventoryTransfer.historyInformation,
                ]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.inventoryTransfers(t));
                }
                break;
        }

        // customerOnboardingUpdates
        switch (location.pathname) {
            case routes.settings.organization:
            case routes.customers.members.base:
            case routes.customers.members.activeMembers:
            case routes.customers.members.inactiveMembers:
            case routes.customers.members.passiveMembers:
                tutorials.push(BizLearnVideos.customerOnboardingUpdates(t))
                break;
            default:
                break;
        }

        // productRemovalsFeature
        switch (location.pathname) {
            case routes.settings.import:
                tutorials.push(BizLearnVideos.productRemovalsFeature(t))
                break;
            default:
                params = getParams(location.pathname, [routes.products.single]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.productRemovalsFeature(t));
                }
                break;
        }

        // vinDecoderFeature
        switch (location.pathname) {
            case routes.lookup.search:
                tutorials.push(BizLearnVideos.vinDecoderFeature(t))
                break;
            default:
                break;
        }

        // defaultIndustryConfig
        switch (location.pathname) {
            case routes.settings.personal:
                tutorials.push(BizLearnVideos.defaultIndustryConfig(t))
                break;
            default:
                break;
        }

        // productsShippingFlatRates
        switch (location.pathname) {
            case routes.shippingFees.regionRates:
            case routes.shippingFees.shippingRates.base:
            case routes.shippingFees.shippingRates.regionRates:
            case routes.shippingFees.shippingRates.productFlatRates:
                tutorials.push(BizLearnVideos.productsShippingFlatRates(t))
                break;
            default:
                params = getParams(location.pathname, [
                    routes.customer.sales.quotes.single,
                    routes.customer.sales.salesOrder.single,
                ]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.productsShippingFlatRates(t));
                }
                break;
        }

        // bulkImportProductFlatRates
        switch (location.pathname) {
            case routes.settings.import:
                tutorials.push(BizLearnVideos.bulkImportProductFlatRates(t))
                break;
            default:
                break;
        }

        // freightModuleUpdates
        switch (location.pathname) {
            case routes.shippingFees.companies:
                tutorials.push(BizLearnVideos.freightModuleUpdates(t))
                break;
            default:
                params = getParams(location.pathname, [
                    routes.customer.sales.quotes.single,
                    routes.customer.sales.salesOrder.single,
                    routes.customer.sales.invoices.single,
                ]);
                if (!!params) {
                    tutorials.push(BizLearnVideos.freightModuleUpdates(t));
                }
                break;
        }
        
        // systemSearch
        tutorials.push(BizLearnVideos.systemSearch(t));

        // Reverse the tutorials to show the most relevant first
        tutorials.reverse();

        setApplicableTutorials(tutorials)
    }, [getMainTutorialsFromPage, getParams, t]);

    /**
     * With each change in the history:
     * - attaches a listener on the history changes that would change the application's information based on the
     * location changes.
     */
    useLayoutEffect(() => {
        const cleanup = history.listen(getTutorialsFromPage);
        getTutorialsFromPage(history.location);
        return () => cleanup();
    }, [history, getTutorialsFromPage])

    /**
     * With each change in the [passedBreakpoint] value:
     * - Close the popover
     */
    useEffect(() => {
        setPopoverAnchor(undefined)
    }, [passedBreakpoint])

    /**
     * Update the popover position on window resize.
     */
    useEffect(() => {
        if (!popoverAnchor)
            return;
        const debounced = debounce(() => {
            popoverAction.current?.updatePosition()
        }, 1000)
        window.addEventListener('resize', debounced);
        return () => window.removeEventListener('resize', debounced);
    }, [popoverAnchor])

    /**
     * Open the plugins popover
     */
    const openPopover = useCallback((e, anchorElement) => {
        setPopoverAnchor(anchorElement ?? e.currentTarget);
    }, [])

    /**
     * Close the plugins popover
     */
    const closePopover = useCallback(() => {
        setPopoverAnchor(undefined)
    }, [])

    const tutorialsPopover = useMemo(() => (
        <TutorialsPopover
            open={!!popoverAnchor}
            anchorEl={popoverAnchor}
            onClose={closePopover}
            popoverAction={popoverAction}
            applicableTutorials={applicableTutorials}
            allTutorials={allTutorials}
        />
    ), [popoverAnchor, closePopover, applicableTutorials, allTutorials]);


    return useMemo<EndActionType | null>(() =>
            !operatorAccessLevels.packages.base.connectedAppsBizLearn
                ? null
                : ({
                    key: "biz-learn-videos-action",
                    title: t($ => $.title, {count: 2}),
                    icon: popoverAnchor ? <OndemandVideoRounded/> : <OndemandVideoOutlined/>,
                    color: "primary",
                    onClick: openPopover,
                    children: tutorialsPopover,
                }),
        [tutorialsPopover, openPopover, operatorAccessLevels.packages.base.connectedAppsBizLearn, popoverAnchor, t])
}

export default useBizLearnVideosEndAction;
